/**
 * The resumable upload client, shared by every surface that sends bytes.
 *
 * Written for the prospect surface in stage 7 and moved here in DG.1, when a representative got an
 * upload of their own. Two copies would have been the wrong answer for the same reason the API client
 * is shared: almost all of this is failure handling, and failure handling is the part that never runs
 * during development and runs on the worst day. The zone rule forbids `frontend/*` importing each
 * other, so this library is the only place both can reach.
 *
 * Hand-rolled rather than `tus-js-client`, and the reason is the failure this has to survive. The
 * server is the authority on the offset: section 09 answers a mismatched `Upload-Offset` with a
 * conflict rather than accepting it, because appending at the wrong place corrupts the object and
 * nothing notices until the hash comparison at the very end of a 350MB transfer. A client that tracked
 * its own offset and trusted it would be the thing that produces that. So this one asks - `HEAD` before
 * every attempt, and again after any failure - and never assumes.
 *
 * The other reason is size. This is a mobile-first surface a prospect loads over cellular, and the
 * whole of the protocol we use is three requests: `POST` to create, `HEAD` for the offset, `PATCH` to
 * append. A dependency for that is a dependency to keep updated.
 *
 * ## What a surface has to supply
 *
 * Everything that differs between the two callers, and nothing else. The prospect's session is a cookie
 * and their endpoints are same-origin under `/guest/v1`; the representative's credential is a header
 * and their endpoints are absolute, cross-origin, and rooted at an exchange. Both are `UploadTransport`.
 *
 * Errors stay the surface's own. This module never constructs one a caller would have to translate: it
 * asks the transport for one and carries it out unchanged, so a prospect still sees the sentence
 * `guest-failures.ts` wrote and a representative still gets a correlation id on the screen.
 *
 * ## What "surviving a dropped connection" means here
 *
 * A phone that loses signal mid-chunk gets a rejected `fetch`, not a status. The chunk is retried from
 * the offset the server reports, with a bounded backoff, and the loop continues. A prospect on a train
 * who loses signal at 200MB resumes rather than restarts, which is section 09's stated goal and the
 * reason tus is here at all.
 *
 * Retries are bounded per chunk rather than overall: an upload that has made progress should keep
 * going indefinitely, and one that cannot move at all should stop and say so.
 */
import { TUS_CHUNK_BYTES, TUS_PATCH_CONTENT_TYPE, TUS_VERSION, uploadCreatedSchema, uploadStatusSchema } from '@iqz-ddds/contracts';
import type { UploadStatus } from '@iqz-ddds/contracts';

/** Consecutive failures on one chunk before giving up. Beyond this the network is not coming back. */
const MAX_CHUNK_ATTEMPTS = 5;

/** Backoff between attempts on one chunk. Short: a person is watching a progress bar. */
const BACKOFF_MS = [400, 1_000, 2_500, 5_000];

/**
 * States where the pipeline has stopped and there is something to say.
 *
 * `held` is included because it is an answer - an operator has it now - even though it is not an
 * ending. `failed` deliberately is not: section 09 makes commit idempotent and the scheduler retries,
 * so it is a state to keep waiting through rather than one to report.
 */
const SETTLED = new Set(['committed', 'rejected', 'held']);

/**
 * How long to wait between asking, and for how long in total.
 *
 * Tight at first because the stub scanner answers in milliseconds and most files are small, then
 * slower, because a 350MB scan is not going to finish sooner for being asked more often. The last
 * interval repeats. The budget exists because a person should not poll indefinitely - past it the
 * surface says so and stops, and the document appears on the next visit.
 */
const POLL_BACKOFF_MS = [400, 400, 800, 1_500, 2_500, 4_000];
const POLL_BUDGET_MS = 120_000;

/**
 * How one surface reaches its own upload endpoints, and what it calls a failure.
 *
 * Deliberately small. Everything above this line is protocol and is identical for a prospect and a
 * representative; everything here is the part that is not.
 */
export interface UploadTransport {
  /** Where a creation `POST` goes. The prospect's collection, or a representative's exchange. */
  readonly createUrl: string;
  /** Where per-upload requests go. `/{uploadId}` is appended, and `/commit` after that. */
  readonly uploadsUrl: string;
  /**
   * Issue a request, authorised however this surface authorises.
   *
   * Rejects only when the request never completed. A 4xx or 5xx is a resolved promise, because that is
   * what `fetch` does and because the difference decides whether a retry is worth making.
   */
  send(url: string, init: RequestInit): Promise<Response>;
  /**
   * The same, reporting bytes as they leave. Optional, and only the chunk `PATCH` uses it.
   *
   * DG.21: progress came from the server's `Upload-Offset` after each chunk completed, and a chunk is
   * 8MB - so nothing moved for the first several seconds of a large upload on a slow connection, which
   * is the exact moment the surface most needs to look alive. `fetch` cannot report it, so a transport
   * that wants in-chunk progress implements this with `xhrSend`.
   *
   * Optional rather than required so that a surface which has not implemented it keeps the old
   * behaviour instead of failing to compile, and so that this stays a presentation improvement rather
   * than something the protocol depends on. `sentBytes` is *in flight*, not accepted: the server is
   * still the authority on the offset and the loop reconciles to it when the chunk lands.
   */
  sendWithProgress?(url: string, init: RequestInit, onSent: (bytes: number) => void): Promise<Response>;
  /** This surface's own error for a response that was not ok. May read the body for problem details. */
  failure(response: Response, fallback: string): Promise<Error>;
  /** This surface's own error for a request that never reached the server. */
  offline(): Error;
}

/**
 * A failed request, on its way out of the loop.
 *
 * Carries the status so the retry decision can be made on the protocol rather than on the shape of
 * somebody's error class, and carries the surface's own error so what finally reaches the caller is
 * the sentence that surface wrote. Never escapes this module.
 */
class Attempt extends Error {
  constructor(
    readonly status: number,
    readonly surfaceError: Error,
  ) {
    super(surfaceError.message);
    this.name = 'Attempt';
  }
}

export interface UploadProgress {
  /**
   * Bytes the server has confirmed, plus whatever is in flight in the current chunk.
   *
   * The two are separated by `confirmedBytes` below rather than being one number, because
   * they are different claims: everything up to `confirmedBytes` is durable and resumable,
   * and the rest is bytes that have left this machine and may yet be lost. A bar that drew
   * them the same would be asserting the second is as good as the first, which is the
   * assumption section 09's whole tus decision exists to avoid.
   */
  readonly sentBytes: number;
  /** What the server has acknowledged. Never decreases except after a resume. */
  readonly confirmedBytes: number;
  readonly totalBytes: number;
}

/**
 * How an upload ended, once the pipeline stopped or the surface stopped waiting.
 *
 * `state` is section 09's, unchanged, because the surface's copy differs per state and translating it
 * here would put that decision in the wrong file. `detail` is the scanner's own words on a refusal or a
 * hold, and null otherwise.
 */
export interface UploadHandle {
  readonly uploadId: string;
  readonly state: string;
  readonly documentId: string | null;
  readonly detail: string | null;
  /** True when the budget ran out with the pipeline still working. Not a failure. */
  readonly stillChecking: boolean;
}

export interface UploadRequest {
  readonly file: File;
  /** The checklist item this answers, where a prospect chose one. Never set by a representative. */
  readonly requestId?: string;
  readonly onProgress?: (progress: UploadProgress) => void;
  /** Called once the bytes are all sent and the wait for a verdict begins. */
  readonly onChecking?: () => void;
  /** Aborts between chunks. Somebody who navigates away should not keep uploading. */
  readonly signal?: AbortSignal;
}

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/** The progress-reporting form, with the same rejection contract as `send`. */
async function sendWatched(
  transport: UploadTransport,
  url: string,
  init: RequestInit,
  onSent: (bytes: number) => void,
): Promise<Response> {
  try {
    // Non-null: the caller checked, and narrowing does not survive the call boundary.
    return await transport.sendWithProgress!(url, init, onSent);
  } catch {
    throw new Attempt(0, transport.offline());
  }
}

/** Issue a request, turning a rejected `fetch` into the surface's offline error. */
async function send(transport: UploadTransport, url: string, init: RequestInit): Promise<Response> {
  try {
    return await transport.send(url, init);
  } catch {
    // `fetch` rejects only when the request never completed: no network, DNS, a dropped connection.
    // Status 0 is not a real one, and it is what marks this recoverable below.
    throw new Attempt(0, transport.offline());
  }
}

/**
 * The server's offset for an upload.
 *
 * `HEAD`, per tus. This is the authority, and asking it after a failure is what makes a resume correct
 * rather than hopeful: the server may have received part of a chunk the client believes failed.
 */
async function offsetOf(transport: UploadTransport, uploadId: string): Promise<number> {
  const response = await send(transport, `${transport.uploadsUrl}/${uploadId}`, {
    method: 'HEAD',
    headers: { 'Tus-Resumable': TUS_VERSION },
  });

  if (!response.ok) {
    throw new Attempt(response.status, await transport.failure(response, 'This upload could no longer be found.'));
  }
  return offsetHeader(transport, response);
}

/** `Upload-Offset`, or the surface's error. Falling back to a local count is the assumption this avoids. */
async function offsetHeader(transport: UploadTransport, response: Response): Promise<number> {
  const raw = response.headers.get('Upload-Offset');
  const offset = raw === null ? Number.NaN : Number(raw);
  if (!Number.isInteger(offset) || offset < 0) {
    throw new Attempt(
      response.status,
      await transport.failure(response, 'The server did not say how much of this file it has.'),
    );
  }
  return offset;
}

/**
 * Send one chunk at `offset` and return the offset the server reports afterwards.
 *
 * `onSent` is called with bytes in flight *within this chunk*, which is DG.21's whole point: a chunk is
 * 8MB and reporting only on completion meant nothing moved for the first several seconds of a large
 * upload. It is a presentation signal and nothing else - the return value is still the server's offset,
 * read from `Upload-Offset`, and it is what the loop advances on.
 */
async function sendChunk(
  transport: UploadTransport,
  uploadId: string,
  file: File,
  offset: number,
  onSent?: (bytesInChunk: number) => void,
): Promise<number> {
  const end = Math.min(offset + TUS_CHUNK_BYTES, file.size);
  const url = `${transport.uploadsUrl}/${uploadId}`;
  const init: RequestInit = {
    method: 'PATCH',
    headers: {
      'Tus-Resumable': TUS_VERSION,
      'Upload-Offset': String(offset),
      'Content-Type': TUS_PATCH_CONTENT_TYPE,
    },
    // `slice` is a view, not a copy. A 350MB file is never held in memory twice.
    body: file.slice(offset, end),
  };

  const response =
    transport.sendWithProgress === undefined || onSent === undefined
      ? await send(transport, url, init)
      : await sendWatched(transport, url, init, onSent);

  if (!response.ok) {
    // 409 is the offset conflict, and it is recoverable rather than fatal: the caller re-reads the
    // offset and continues from wherever the server actually is.
    throw new Attempt(
      response.status,
      await transport.failure(
        response,
        response.status === 409
          ? 'This upload is out of step. Retrying from where the server is.'
          : 'That part of the file was not accepted.',
      ),
    );
  }

  return offsetHeader(transport, response);
}

/**
 * Whether another attempt on this chunk is worth making.
 *
 * On the status rather than on the error's class. A conflict resolves itself once the offset is
 * re-read, 5xx is worth retrying, 0 is the dropped connection tus exists for, and a 4xx that is not a
 * conflict - the session lapsed, the upload expired - will not improve.
 */
function isRecoverable(err: unknown): boolean {
  if (!(err instanceof Attempt)) return false;
  return err.status === 0 || err.status === 409 || err.status >= 500;
}

/** Unwrap, so what leaves this module is always the surface's own error. */
function surfaced(err: unknown): unknown {
  return err instanceof Attempt ? err.surfaceError : err;
}

/**
 * Upload a file, resuming across dropped connections, and commit it.
 *
 * Refuses a file over the server's ceiling before sending anything, which is section 09's instruction:
 * "Reject uploads declaring more than maxObjectBytes up front rather than at the end." Asking a person
 * on cellular to send 400MB before telling them it is too large is the rudest possible way to enforce a
 * limit.
 */
export async function uploadFile(
  transport: UploadTransport,
  input: UploadRequest & { readonly maxBytes?: number; readonly tooLarge?: (size: string, max: string) => Error },
): Promise<UploadHandle> {
  const { file, maxBytes } = input;

  // Optional, because a caller cannot know the ceiling before it has created an upload - the server
  // reports it in that response. The server rejects an oversized `Upload-Length` up front regardless,
  // which is the actual control; this only saves a round trip once the surface has learned the value.
  if (maxBytes !== undefined && file.size > maxBytes && input.tooLarge !== undefined) {
    throw input.tooLarge(formatBytes(file.size), formatBytes(maxBytes));
  }

  try {
    return await run(transport, input);
  } catch (err) {
    throw surfaced(err);
  }
}

async function run(transport: UploadTransport, input: UploadRequest): Promise<UploadHandle> {
  const { file, onProgress, signal } = input;

  // Headers, not a body. tus creation declares the length in `Upload-Length` and everything else in
  // `Upload-Metadata`, and the server parses exactly that - a JSON body here is answered with a 415.
  const createResponse = await send(transport, transport.createUrl, {
    method: 'POST',
    headers: {
      'Tus-Resumable': TUS_VERSION,
      'Upload-Length': String(file.size),
      'Upload-Metadata': encodeMetadata({
        filename: file.name,
        // The browser's guess, and the server treats it as one: the scanner reads the bytes and the
        // converter sniffs the container. An empty type happens on Android for odd extensions.
        filetype: file.type === '' ? 'application/octet-stream' : file.type,
      }),
    },
  });

  if (!createResponse.ok) {
    throw new Attempt(createResponse.status, await transport.failure(createResponse, 'This upload could not be started.'));
  }

  const created = uploadCreatedSchema.parse(await createResponse.json());
  const { uploadId } = created;
  let offset = 0;
  let attempts = 0;

  /**
   * Both numbers, every time, so the caller never has to remember which is which.
   *
   * `inChunk` is bytes in flight and is discarded the moment the server answers: the chunk's own
   * count is only ever an estimate of a value the next line replaces with the real one.
   */
  const report = (confirmed: number, inChunk = 0): void => {
    onProgress?.({
      // Clamped, because a chunk's `loaded` counts framing overhead as well as body bytes and can
      // exceed the slice. A bar that reached 101% would be visibly wrong.
      sentBytes: Math.min(confirmed + inChunk, file.size),
      confirmedBytes: confirmed,
      totalBytes: file.size,
    });
  };

  report(0);

  while (offset < file.size) {
    signal?.throwIfAborted();
    // Captured per iteration: the closure below must not report against an offset the loop has
    // since advanced past, which would make the bar jump forward and then back.
    const chunkStart = offset;

    try {
      offset = await sendChunk(transport, uploadId, file, offset, (bytesInChunk) => report(chunkStart, bytesInChunk));
      // Reset on progress, so a long upload over a flaky connection is not killed by five
      // non-consecutive failures spread across an hour.
      attempts = 0;
      report(offset);
    } catch (err) {
      attempts += 1;
      if (!isRecoverable(err) || attempts >= MAX_CHUNK_ATTEMPTS) throw err;

      await sleep(BACKOFF_MS[Math.min(attempts - 1, BACKOFF_MS.length - 1)] ?? 5_000);
      signal?.throwIfAborted();

      // The authority, after every failure. The server may hold part of the chunk we think failed.
      // This is also the one place the bar may go *backwards*, and it should: the in-flight bytes
      // reported a moment ago were not accepted, and pretending otherwise is the local counting the
      // whole tus decision avoids.
      offset = await offsetOf(transport, uploadId);
      report(offset);
    }
  }

  const committed = await commit(transport, uploadId, file.name, input.requestId);

  input.onChecking?.();
  return waitForVerdict(transport, uploadId, committed, signal);
}

/** Seal the upload. The pipeline is the server's business from here. */
async function commit(transport: UploadTransport, uploadId: string, name: string, requestId: string | undefined): Promise<UploadStatus> {
  const response = await send(transport, `${transport.uploadsUrl}/${uploadId}/commit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // `requestId` is omitted rather than sent as null for a representative, because `/api/v1` refuses
    // the field outright: a checklist item is the contact's side of the exchange.
    body: JSON.stringify({ name, ...(requestId === undefined ? {} : { requestId }) }),
  });

  if (!response.ok) {
    throw new Attempt(response.status, await transport.failure(response, 'This document could not be saved.'));
  }
  return uploadStatusSchema.parse(await response.json());
}

/**
 * Ask until the pipeline has finished with this upload.
 *
 * This is the half that makes an upload look like it happened. R5 puts a scan between the last chunk
 * and the system of record, so at the moment the commit returns there is no document, the checklist
 * item is still outstanding, and both lists say exactly what they said before the file was sent. A
 * surface that re-read them then would show the person their own upload having vanished - and the
 * reasonable response to that is to send it again. DG.14.
 *
 * The commit's own answer is the first sample rather than a wasted round trip: a small file through a
 * fast scanner can be committed by the time it arrives.
 *
 * Failures while polling are swallowed and retried within the budget. A dropped connection here means
 * the surface does not yet know the outcome, which is not the same as the upload having failed - the
 * bytes are sealed and the pipeline is running regardless of whether anybody is watching.
 */
async function waitForVerdict(
  transport: UploadTransport,
  uploadId: string,
  first: UploadStatus,
  signal?: AbortSignal,
): Promise<UploadHandle> {
  let status = first;
  let waited = 0;
  // Only the backoff step reads this. It is not what ends the loop, which is why the loop is not
  // written around it: what stops this is a verdict or the budget.
  let polls = 0;

  while (!SETTLED.has(status.state) && waited < POLL_BUDGET_MS) {
    const delay = POLL_BACKOFF_MS[Math.min(polls, POLL_BACKOFF_MS.length - 1)] ?? 4_000;
    polls += 1;
    await sleep(delay);
    waited += delay;
    signal?.throwIfAborted();

    try {
      const response = await send(transport, `${transport.uploadsUrl}/${uploadId}`, { method: 'GET' });
      // A lapsed credential will not improve, and asking again would spin until the budget ran out.
      if (response.status === 401) {
        throw new Attempt(401, await transport.failure(response, 'This session has ended.'));
      }
      if (response.ok) status = uploadStatusSchema.parse(await response.json());
    } catch (err) {
      if (err instanceof Attempt && err.status === 401) throw err;
    }
  }

  return {
    uploadId,
    state: status.state,
    documentId: status.documentId,
    detail: status.detail,
    stillChecking: !SETTLED.has(status.state),
  };
}

/**
 * `Upload-Metadata`, per tus: comma-separated `key base64(value)` pairs.
 *
 * `TextEncoder` before base64, because a filename is very often not ASCII - an accented name, or a
 * photograph named in another script - and `btoa` throws on anything above U+00FF. Getting this wrong
 * would break exactly the uploads most worth getting right.
 */
function encodeMetadata(pairs: Readonly<Record<string, string>>): string {
  return Object.entries(pairs)
    .map(([key, value]) => {
      const bytes = new TextEncoder().encode(value);
      const binary = Array.from(bytes, (byte) => String.fromCodePoint(byte)).join('');
      return `${key} ${btoa(binary)}`;
    })
    .join(',');
}

/** Bytes, for a sentence a person reads. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;

  const mb = bytes / (1024 * 1024);
  if (mb < 1) return `${Math.round(bytes / 1024)} KB`;

  // One decimal below 10MB and none above it: "8.4 MB" is worth reading and "347.2 MB" is not.
  const places = mb < 10 ? 1 : 0;
  return `${mb.toFixed(places)} MB`;
}
